import _ from 'lodash';

import {compareName} from '../../../../srcCommon/helpers/localeHelpers';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {ActionWithPayload, batch} from '../../../helpers/reduxHelpers';
import {
  ApiCustomField,
  ApiCustomFields,
  CustomFieldBaseModel,
  CustomFieldModel,
  CustomFieldResourceTypesEnum,
} from '../../../models/api/customFieldModel';
import {ApiSendOptions, deleteAsync, fetchApiAsync, postApiAsync} from '../../../services/http/http';
import {HttpError} from '../../../services/http/httpError';
import {customFieldResourceTypeToFetchPathRouteMap} from '../../../settings/customFields/customFieldsHelpers';
import {ThunkAction, ThunkAsyncAction} from '../../appThunk';
import {getCompanyUrl} from '../company/companySelectors';
import {updateDataAction} from '../dataGroupsActions';

/*
 * Action types.
 */

export enum CustomFieldsActionsEnum {
  CUSTOM_FIELD_UPDATED = '@@data/customFields/CUSTOM_FIELD_UPDATED',
  CUSTOM_FIELD_DELETED = '@@data/customFields/CUSTOM_FIELD_DELETED',
}

/*
 * Types.
 */

export interface CustomFieldUpdatedAction extends ActionWithPayload<CustomFieldModel> {
  type: CustomFieldsActionsEnum.CUSTOM_FIELD_UPDATED;
}

export interface CustomFieldsDeletedAction extends ActionWithPayload<number> {
  type: CustomFieldsActionsEnum.CUSTOM_FIELD_DELETED;
}

export type CreateCustomFieldAsyncAction = (
  customFieldData: CustomFieldBaseModel,
  resourceType: CustomFieldResourceTypesEnum,
  options?: ApiSendOptions,
) => Promise<number>;

/*
 * Action Creators.
 */

function customFieldDeletedAction(customFieldId: number): CustomFieldsDeletedAction {
  return {
    type: CustomFieldsActionsEnum.CUSTOM_FIELD_DELETED,
    payload: customFieldId,
  };
}

/*
 * Action.
 */

export function fetchCustomFieldsForResourceTypeAsyncAction(
  resourceType: CustomFieldResourceTypesEnum,
): ThunkAsyncAction<ReadonlyArray<number>> {
  return async (dispatch, getState) => {
    const {company} = getState().data;
    const fetchCustomFieldsUrl = `${company.url}/${customFieldResourceTypeToFetchPathRouteMap[resourceType]}`;

    try {
      const customFields = await fetchApiAsync(ApiCustomFields, fetchCustomFieldsUrl);
      dispatch(updateDataAction({customFields}));

      return _([...customFields])
        .sort((a, b) => compareName(a.name.toLowerCase(), b.name.toLowerCase()))
        .map((customField) => customField.id)
        .value();
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function updateCustomFieldAction(newCustomField: CustomFieldModel): ThunkAction {
  return (dispatch, getState) => {
    // Find the existing custom field.
    const existingCustomField = getState().data.customFields[newCustomField.id];

    // No need to do anything if the new custom field is older.
    if (existingCustomField && existingCustomField.updatedAt > newCustomField.updatedAt) {
      return null;
    }

    const action: CustomFieldUpdatedAction = {
      type: CustomFieldsActionsEnum.CUSTOM_FIELD_UPDATED,
      payload: {...existingCustomField, ...newCustomField},
    };
    return dispatch(action);
  };
}

export function deleteCustomFieldAction(customFieldId: number): ThunkAction {
  return (dispatch, getState) => {
    // If we don't have this custom field, nothing to do.
    const existingCustomField = getState().data.customFields[customFieldId];
    if (!existingCustomField) {
      return null;
    }

    return dispatch(customFieldDeletedAction(customFieldId));
  };
}

export function deleteCustomFieldsAsyncAction(customFieldIds: ReadonlyArray<number>): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());

    // We have no multi delete endpoint, this issues multiple events.
    const deleteRequests = customFieldIds.map((customFieldId) =>
      deleteAsync(`${companyUrl}/custom_fields/${customFieldId}`),
    );

    try {
      await Promise.all(deleteRequests);
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
    }

    batch(() => {
      customFieldIds.forEach((customFieldId) => dispatch(customFieldDeletedAction(customFieldId)));
    });
  };
}

export function createCustomFieldAsyncAction(
  customFieldData: CustomFieldBaseModel,
  resourceType: CustomFieldResourceTypesEnum,
  options?: ApiSendOptions,
): ThunkAsyncAction<number> {
  return async (dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());

    try {
      const customField = await postApiAsync(
        ApiCustomField,
        `${companyUrl}/${customFieldResourceTypeToFetchPathRouteMap[resourceType]}`,
        customFieldData,
        options,
      );
      dispatch(updateDataAction({customFields: [customField]}));
      return customField.id;
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

/*
 * Union type.
 */

export type CustomFieldsAction = CustomFieldUpdatedAction | CustomFieldsDeletedAction;
